import React from 'react';

import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

interface PhoneNumberInputProps extends Omit<React.ComponentProps<'input'>, 'onChange' | 'value'> {
  value?: string;
  onChange?: (value: string) => void;
}

/**
 * Calculates the cursor position in the formatted string based on the number of digits
 * before the cursor in the original string.
 */
function calculateCursorPosition(formatted: string, digitsBeforeCursor: number): number {
  let digitCount = 0;
  for (let i = 0; i < formatted.length; i++) {
    if (/\d/.test(formatted[i] ?? '')) {
      digitCount++;
      if (digitCount === digitsBeforeCursor) {
        return i + 1;
      }
    }
  }
  return formatted.length;
}

export const PhoneNumberInput = ({
  value,
  onChange,
  className,
  ...props
}: PhoneNumberInputProps) => {
  const [displayValue, setDisplayValue] = React.useState(formatPhoneNumber(value));
  const cursorPositionRef = React.useRef<number | null>(null);
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  React.useEffect(() => {
    setDisplayValue(formatPhoneNumber(value));
  }, [value]);

  React.useLayoutEffect(() => {
    if (cursorPositionRef.current !== null && inputRef.current) {
      inputRef.current.setSelectionRange(cursorPositionRef.current, cursorPositionRef.current);
      cursorPositionRef.current = null;
    }
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const cursorPosition = input.selectionStart ?? 0;
    const inputValue = input.value;

    inputRef.current = input;

    // Count digits before cursor to preserve position across formatting
    const textBeforeCursor = inputValue.slice(0, cursorPosition);
    const digitsBeforeCursor = textBeforeCursor.replace(NON_DIGIT_CHARACTERS, '').length;

    // Extract raw digits (unformatted value) for onChange callback
    const rawValue = inputValue.replace(NON_DIGIT_CHARACTERS, '').slice(0, 10);

    const formatted = formatPhoneNumber(inputValue);
    const newCursorPosition = calculateCursorPosition(formatted, digitsBeforeCursor);

    cursorPositionRef.current = newCursorPosition;
    setDisplayValue(formatted);
    onChange?.(rawValue);
  };

  return (
    <Input
      type="tel"
      inputMode="tel"
      onChange={handleChange}
      value={displayValue}
      className={cn(className)}
      placeholder="(000) 000-0000"
      {...props}
    />
  );
};

PhoneNumberInput.displayName = 'PhoneNumberInput';

const NON_DIGIT_CHARACTERS = /[^\d]/g;
function formatPhoneNumber(phoneNumber: string | null | undefined) {
  if (!phoneNumber) return '';
  const digits = phoneNumber.replace(NON_DIGIT_CHARACTERS, '').slice(0, 10);

  if (digits.length === 0) return '';
  if (digits.length < 4) return digits;
  if (digits.length < 7) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  }
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}
